import React, { useEffect, useMemo, useState } from "react";
import { Download, Eye, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

type Applicant = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  position: string;
  coverLetter?: string;
  resumePath?: string | null;
  resumeUrl?: string | null;
  // Optional structured responses to per-job custom questions
  // Either an array of { question, answer } or a raw string
  questionResponses?: any;
  createdAt: string;
};

const PAGE_SIZES = [5, 10, 20, 50];

const AdminApplicants: React.FC = () => {
  const { token } = useAuth();
  const [rawApplicants, setRawApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState<string>("All");
  const [sortBy, setSortBy] = useState<"date_desc" | "date_asc" | "name_asc" | "name_desc">("date_desc");
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [selected, setSelected] = useState<Applicant | null>(null);

  useEffect(() => {
    fetchApplicants();
  }, []);

  const getFilenameFromPath = (p?: string | null): string | null => {
    if (!p) return null;
    const parts = p.split(/[/\\]+/).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  };

  const buildResumeUrl = (app: Applicant): string | null => {
    if (app.resumeUrl) return app.resumeUrl;
    const filename = getFilenameFromPath(app.resumePath ?? null);
    if (!filename) return null;
    return `${BACKEND_URL}/uploads/${encodeURIComponent(filename)}`;
  };

  const fetchApplicants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/applicants`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error(`Failed to fetch applicants (${res.status})`);
      const data = (await res.json()) as any[];

      const normalized: Applicant[] = data.map((d) => {
        const filename = getFilenameFromPath(d.resumePath ?? null);
        const normalizedResumeUrl = d.resumeUrl != null
          ? d.resumeUrl
          : filename
          ? `${BACKEND_URL}/uploads/${encodeURIComponent(filename)}`
          : null;

        return {
          id: Number(d.id ?? 0),
          firstName: d.firstName ?? "",
          lastName: d.lastName ?? "",
          email: d.email ?? "",
          phone: d.phone ?? undefined,
          position: d.position ?? "",
          coverLetter: d.coverLetter ?? undefined,
          resumePath: d.resumePath ?? filename ?? undefined,
          resumeUrl: normalizedResumeUrl,
          questionResponses: d.questionResponses,
          createdAt: d.createdAt ? new Date(d.createdAt).toISOString() : new Date().toISOString(),
        };
      });

      setRawApplicants(normalized);
    } catch (err: any) {
      setError(err?.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const positions = useMemo(() => {
    const setPos = new Set<string>();
    rawApplicants.forEach((a) => setPos.add(a.position || "Unknown"));
    return ["All", ...Array.from(setPos).sort()];
  }, [rawApplicants]);

  const filtered = useMemo(() => {
    let list = [...rawApplicants];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (a) =>
          `${a.firstName} ${a.lastName}`.toLowerCase().includes(q) ||
          (a.email || "").toLowerCase().includes(q) ||
          (a.position || "").toLowerCase().includes(q) ||
          (a.coverLetter || "").toLowerCase().includes(q)
      );
    }

    if (positionFilter !== "All") {
      list = list.filter((a) => a.position === positionFilter);
    }

    switch (sortBy) {
      case "date_desc":
        list.sort((x, y) => +new Date(y.createdAt) - +new Date(x.createdAt));
        break;
      case "date_asc":
        list.sort((x, y) => +new Date(x.createdAt) - +new Date(y.createdAt));
        break;
      case "name_asc":
        list.sort((x, y) =>
          `${x.firstName} ${x.lastName}`.localeCompare(`${y.firstName} ${y.lastName}`)
        );
        break;
      case "name_desc":
        list.sort((x, y) =>
          `${y.firstName} ${y.lastName}`.localeCompare(`${x.firstName} ${x.lastName}`)
        );
        break;
    }

    return list;
  }, [rawApplicants, search, positionFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const renderQuestionResponsesSummary = (app: Applicant): string => {
    const qr = app.questionResponses;
    if (!qr) return "";
    if (Array.isArray(qr)) {
      // Show a compact summary: number of answered questions
      const answered = qr.filter((x: any) => x && x.answer && String(x.answer).trim().length > 0).length;
      if (answered === 0) return "";
      return `${answered} question${answered === 1 ? "" : "s"} answered`;
    }
    // Fallback: treat as plain text
    return String(qr);
  };

  const renderQuestionResponsesDetail = (app: Applicant) => {
    const qr = app.questionResponses;
    if (!qr) return null;

    if (Array.isArray(qr)) {
      if (qr.length === 0) return null;
      return (
        <div className="space-y-3">
          {qr.map((item: any, index: number) => {
            if (!item || (!item.question && !item.answer)) return null;
            return (
              <div
                key={index}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'var(--pw-surface-secondary)',
                  color: 'var(--pw-text-main)',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
              >
                {item.question && (
                  <p
                    className="text-sm font-medium mb-1"
                    style={{ color: 'var(--pw-muted-light)' }}
                  >
                    {item.question}
                  </p>
                )}
                {item.answer && (
                  <p className="whitespace-pre-wrap text-sm">
                    {item.answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div
        className="p-3 rounded-lg whitespace-pre-wrap"
        style={{
          backgroundColor: 'var(--pw-surface-secondary)',
          color: 'var(--pw-text-main)',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        {String(qr)}
      </div>
    );
  };

  const downloadCSV = () => {
    const rows = [
      ["ID", "First Name", "Last Name", "Email", "Phone", "Position", "Created At", "Resume URL", "Cover Letter"],
      ...filtered.map((a) => [
        String(a.id ?? ""),
        a.firstName ?? "",
        a.lastName ?? "",
        a.email ?? "",
        a.phone ?? "",
        a.position ?? "",
        a.createdAt ?? "",
        a.resumeUrl ?? buildResumeUrl(a) ?? "",
        (a.coverLetter || "").replace(/\r?\n/g, "\\n"),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `applicants_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearch("");
    setPositionFilter("All");
    setSortBy("date_desc");
    setPage(1);
  };

  if (loading && rawApplicants.length === 0) {
    return (
      <div 
        className="text-center py-8" 
        style={{ 
          color: 'var(--pw-muted-light)',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        Loading applicants...
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="text-center py-8" 
        style={{ 
          color: '#ef4444',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header with filters */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-3xl font-bold mb-2" 
              style={{ 
                color: 'var(--pw-text-main)',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                letterSpacing: '-0.02em'
              }}
            >
              Applicants
            </h1>
            <p 
              className="text-sm" 
              style={{ 
                color: 'var(--pw-muted-light)',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              {filtered.length} total applicant(s)
            </p>
          </div>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all duration-200 font-medium shadow-lg"
            style={{ 
              backgroundColor: 'var(--pw-secondary)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-secondary-light)';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 64, 175, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-secondary)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(30, 64, 175, 0.2)';
            }}
          >
            <Download className="w-5 h-5" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <input
            className="px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors flex-1 min-w-[200px]"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
            placeholder="Search by name, email, position..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <select
            className="px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
            value={positionFilter}
            onChange={(e) => { setPositionFilter(e.target.value); setPage(1); }}
          >
            {positions.map((p) => <option key={p} value={p} style={{ backgroundColor: 'var(--pw-surface-primary)' }}>{p}</option>)}
          </select>
          <select
            className="px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
          >
            <option value="date_desc" style={{ backgroundColor: 'var(--pw-surface-primary)' }}>Newest First</option>
            <option value="date_asc" style={{ backgroundColor: 'var(--pw-surface-primary)' }}>Oldest First</option>
            <option value="name_asc" style={{ backgroundColor: 'var(--pw-surface-primary)' }}>Name A–Z</option>
            <option value="name_desc" style={{ backgroundColor: 'var(--pw-surface-primary)' }}>Name Z–A</option>
          </select>
          <select
            className="px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {PAGE_SIZES.map((s) => <option key={s} value={s} style={{ backgroundColor: 'var(--pw-surface-primary)' }}>{s} per page</option>)}
          </select>
          <button
            onClick={clearFilters}
            className="px-5 py-2.5 rounded-xl transition-all duration-200 font-medium"
            style={{ 
              backgroundColor: 'var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      {paged.length === 0 ? (
        <div 
          className="rounded-xl p-12 text-center"
          style={{ 
            background: 'var(--pw-gradient-card)',
            border: '1px solid var(--pw-surface-tertiary)',
            boxShadow: 'var(--pw-shadow)'
          }}
        >
          <p 
            style={{ 
              color: 'var(--pw-muted-light)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            No applicants found matching your filters.
          </p>
        </div>
      ) : (
        <div 
          className="rounded-2xl overflow-hidden backdrop-blur-sm"
          style={{ 
            background: 'var(--pw-gradient-card)',
            border: '1px solid var(--pw-surface-tertiary)',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <table className="w-full">
            <thead style={{ backgroundColor: 'var(--pw-surface-secondary)' }}>
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Name
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Email
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Position
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Date
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody style={{ borderTop: '1px solid var(--pw-surface-tertiary)' }}>
              {paged.map((app) => (
                <tr 
                  key={app.id}
                  style={{ borderBottom: '1px solid var(--pw-surface-tertiary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td className="px-6 py-4">
                    <div 
                      className="font-medium" 
                      style={{ 
                        color: 'var(--pw-text-main)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      {app.firstName} {app.lastName}
                    </div>
                    <div 
                      className="text-sm" 
                      style={{ 
                        color: 'var(--pw-muted-light)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      {app.phone || '—'}
                    </div>
                  </td>
                  <td 
                    className="px-6 py-4" 
                    style={{ 
                      color: 'var(--pw-text-main)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    {app.email}
                  </td>
                  <td 
                    className="px-6 py-4" 
                    style={{ 
                      color: 'var(--pw-text-main)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    {app.position}
                  </td>
                  <td 
                    className="px-6 py-4 text-sm" 
                    style={{ 
                      color: 'var(--pw-text-main)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    {new Date(app.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm" style={{ color: 'var(--pw-muted-light)' }}>
                    {renderQuestionResponsesSummary(app) || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelected(app)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--pw-secondary-light)' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <a
                        href={`mailto:${app.email}`}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--pw-success-light)' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="Send Email"
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                      {buildResumeUrl(app) && (
                        <a
                          href={buildResumeUrl(app) as string}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 rounded-lg transition-colors"
                          style={{ color: 'var(--pw-primary-light)' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Download Resume"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div 
          className="text-sm" 
          style={{ 
            color: 'var(--pw-muted-light)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
        >
          Showing {(page - 1) * pageSize + 1} – {Math.min(page * pageSize, filtered.length)} of {filtered.length}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-5 py-2.5 rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: 'var(--pw-surface-tertiary)',
              border: '1px solid var(--pw-surface-secondary)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              if (page > 1) e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
            }}
            onMouseLeave={(e) => {
              if (page > 1) e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
            }}
            disabled={page <= 1}
          >
            Previous
          </button>
          <div 
            className="px-5 py-2.5 rounded-xl text-sm font-medium"
            style={{ 
              backgroundColor: 'var(--pw-surface-tertiary)',
              border: '1px solid var(--pw-surface-secondary)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            Page {page} of {totalPages}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-5 py-2.5 rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: 'var(--pw-surface-tertiary)',
              border: '1px solid var(--pw-surface-secondary)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              if (page < totalPages) e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
            }}
            onMouseLeave={(e) => {
              if (page < totalPages) e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
            }}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelected(null)}>
          <div 
            className="relative max-w-2xl w-full rounded-2xl shadow-2xl max-h-[90vh] overflow-auto backdrop-blur-sm"
            style={{ 
              background: 'var(--pw-gradient-card)',
              border: '1px solid var(--pw-surface-tertiary)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(10px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 
                    className="text-2xl font-bold" 
                    style={{ 
                      color: 'var(--pw-text-main)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {selected.firstName} {selected.lastName}
                  </h2>
                  <p 
                    style={{ 
                      color: 'var(--pw-muted-light)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    {selected.position}
                  </p>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{ 
                    backgroundColor: 'var(--pw-surface-tertiary)',
                    color: 'var(--pw-text-main)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
                  }}
                >
                  Close
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p 
                      className="text-sm font-medium" 
                      style={{ 
                        color: 'var(--pw-muted-light)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      Email
                    </p>
                    <p 
                      style={{ 
                        color: 'var(--pw-text-main)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      {selected.email}
                    </p>
                  </div>
                  <div>
                    <p 
                      className="text-sm font-medium" 
                      style={{ 
                        color: 'var(--pw-muted-light)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      Phone
                    </p>
                    <p 
                      style={{ 
                        color: 'var(--pw-text-main)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      {selected.phone || '—'}
                    </p>
                  </div>
                  <div>
                    <p 
                      className="text-sm font-medium" 
                      style={{ 
                        color: 'var(--pw-muted-light)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      Applied Date
                    </p>
                    <p 
                      style={{ 
                        color: 'var(--pw-text-main)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      {new Date(selected.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p 
                      className="text-sm font-medium" 
                      style={{ 
                        color: 'var(--pw-muted-light)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      Resume
                    </p>
                    {buildResumeUrl(selected) ? (
                      <a
                        href={buildResumeUrl(selected) as string}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'var(--pw-primary-light)' }}
                        className="hover:underline"
                      >
                        Download
                      </a>
                    ) : (
                      <p style={{ color: 'var(--pw-muted-light)' }}>—</p>
                    )}
                  </div>
                </div>

                <div>
                  <p 
                    className="text-sm font-medium mb-2" 
                    style={{ 
                      color: 'var(--pw-muted-light)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    Cover Letter
                  </p>
                  <div 
                    className="p-4 rounded-lg whitespace-pre-wrap max-h-64 overflow-y-auto"
                    style={{ 
                      backgroundColor: 'var(--pw-surface-secondary)',
                      color: 'var(--pw-text-main)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    {selected.coverLetter || 'No cover letter provided.'}
                  </div>
                </div>

                {selected.questionResponses && (
                  <div>
                    <p
                      className="text-sm font-medium mb-2"
                      style={{
                        color: 'var(--pw-muted-light)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      Application Questions & Answers
                    </p>
                    {renderQuestionResponsesDetail(selected)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApplicants;

